import * as RadixDialog from '@radix-ui/react-dialog';
import {
  Label as LabelType,
  Control as ControlType,
  Pointer,
  Image,
} from '@yleisradio/areena-types';
import { CardLabels } from 'components/Card/CardLabels';
import { Control } from 'components/Controls/Control/Control';
import { useTranslation } from 'hooks/useTranslation';
import { useEffect, useRef } from 'react';
import { getControlKey } from 'utils/control';
import styles from './StartoverDialog.module.scss';
import { CardImage } from 'components/Card/CardImage';
import { ErrorBoundary } from 'components/ErrorBoundary';
import ProgressBar from 'components/Card/ProgressBar';
import { filterLabelsByType, findLabel } from 'utils/card';
import { randomUUID } from 'utils/uuid';
import {
  clickEventGenerator,
  elementContextGenerator,
  entityContextGenerator,
  viewEventGenerator,
  wrapperContextGenerator,
} from 'services/snowplowAnalytics/contextGenerators';
import { idFromPointer } from 'utils/pointer';

const LIVE_STARTOVER_SEEK_MARGIN_SECS = 30;

type Props = {
  title: string;
  description: string;
  labels: LabelType[];
  cardViewId: string;
  image: Image | undefined;
  playControl: ControlType;
  cardPointer: Pointer | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function StartoverDialog({
  title,
  description,
  labels,
  cardViewId,
  image,
  playControl,
  cardPointer,
  open,
  onOpenChange,
}: Props) {
  const t = useTranslation();
  const triggerElementRef = useRef<HTMLElement>(undefined);

  const genericLabels = [
    ...filterLabelsByType('generic', labels),
    { formatted: description, type: 'generic' as LabelType['type'] },
  ];

  const highlightLabels = filterLabelsByType('highlight', labels);

  const progressLabel = findLabel(labels, 'progress');

  const watchLiveControl: ControlType = {
    ...playControl,
    title: t('common_watch_live'),
  };
  const watchFromStartControl: ControlType = {
    ...playControl,
    icon: { id: 'startover', version: '' },
    title: t('common_start_ondemand_tv'),
  };

  const trackPlayButtonClick = (title: 'play_from_beginning' | 'play_live') => {
    if (!window.yleSnowplow) {
      throw new Error('Snowplow tracker is not available');
    }

    const event = clickEventGenerator('play', cardViewId);

    const context = [
      wrapperContextGenerator({ type: 'overlay', label: 'card_menu' }),
      elementContextGenerator({
        type: 'button',
        title,
        elementIndex: undefined,
        groupIndex: undefined,
        listStyle: undefined,
      }),
    ];

    window.yleSnowplow('trackSelfDescribingEvent', { event, context });
  };

  useEffect(() => {
    if (!window?.yleSnowplow) {
      throw new Error('Snowplow tracker is not available');
    }

    const itemId = idFromPointer(cardPointer);

    if (open && itemId) {
      const viewUuid = randomUUID();
      const viewEvent = viewEventGenerator(viewUuid, 'ui_element', 'overlay');
      const context = [
        wrapperContextGenerator({ type: 'overlay', label: 'card_menu' }),
        entityContextGenerator(itemId, 'engagement_view_source'),
      ];

      window.yleSnowplow('trackSelfDescribingEvent', {
        event: viewEvent,
        context,
      });
    }
  }, [open, cardPointer]);

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={styles.overlay} />
        <RadixDialog.Content
          className={styles.content}
          onOpenAutoFocus={() => {
            const triggerElement = window.document.activeElement;

            if (triggerElement instanceof HTMLElement)
              triggerElementRef.current = triggerElement;
          }}
          onCloseAutoFocus={(e) => {
            const triggerElement = triggerElementRef.current;

            if (triggerElement) {
              triggerElement.focus();
              e.preventDefault();
            }
          }}
        >
          <div className={styles.imageContentWrapper}>
            <CardImage
              image={image}
              imageStyle={'16:9'}
              sizes={
                '(min-width: 1020px) 20vw, (min-width: 640px) 33vw, (min-width: 480px) 41vw, 60vw'
              }
            />
            {highlightLabels.length > 0 ? (
              <div className={styles.highlightWrapper}>
                <CardLabels isHighlight labels={highlightLabels} />
              </div>
            ) : null}
            {progressLabel ? (
              <div className={styles.progressBarWrapper}>
                <ErrorBoundary>
                  <ProgressBar
                    progressLabel={progressLabel}
                    historyElapsed={null}
                  />
                </ErrorBoundary>
              </div>
            ) : null}
          </div>
          <RadixDialog.Title className={styles.title}>
            {title}
          </RadixDialog.Title>
          {genericLabels.length > 0 ? (
            <div className={styles.labelsWrapper}>
              <CardLabels labels={genericLabels} />
            </div>
          ) : null}
          <div className={styles.controls}>
            <Control
              as="button"
              size="sm"
              width="full"
              control={watchLiveControl}
              controlKey={getControlKey(watchLiveControl)}
              cardPointer={cardPointer}
              onClick={() => {
                trackPlayButtonClick('play_live');
                onOpenChange(false);
              }}
            />
            <Control
              as="button"
              size="sm"
              width="full"
              variant="text"
              control={watchFromStartControl}
              controlKey={getControlKey(watchFromStartControl)}
              cardPointer={cardPointer}
              seek={LIVE_STARTOVER_SEEK_MARGIN_SECS}
              onClick={() => {
                trackPlayButtonClick('play_from_beginning');
                onOpenChange(false);
              }}
            />
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
